// apps/vendor/src/pages/auth/RegisterPage.tsx
//
// Phase 1 Wave 9 Task 46 — Vendor register form (mirror of merchant).
// 2026-09-09 vendor-register redesign — now a 2-STEP wizard:
//   Step 1 — general info (email, display name, LEGAL NAME, country,
//            TIMEZONE, phone?, password) — field set mirrors the admin
//            create-vendor wizard Step 1 so both intake forms match.
//   Step 2 — techniques (from GET /api/v1/public/techniques — active only),
//            shipping mode (self_ship | printway_processed | hybrid),
//            first warehouse (mirrors admin wizard first_location).
// Submit happens ONCE at the end of step 2 → POST /api/v1/auth/register with
// the extended payload → /verify-otp → status pending_approval directly (the
// vendor survey step is gone).
//
// Key vendor-specific behaviour:
//   - `intendedRole='vendor'` on the submit body → admin must approve before
//     login is allowed (403 AUTH_VENDOR_PENDING_APPROVAL until then).
//   - A REJECTED email may re-register after the 7-day cooldown; inside the
//     window the BE returns AUTH_VENDOR_REJECTED_COOLDOWN with
//     details.reapply_available_at, surfaced below.
//   - Idempotency-Key rotates per submit (anon-scoped BE middleware).

import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { normalizePhoneE164, PhoneFormatError } from '@printway/shared';
import { apiClient } from '@printway/shared/api';
import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  useToast,
} from '@printway/ui';

import { useTranslation, t as i18nT } from '../../lib/i18n/index.js';
import { csrfFetch } from '../../lib/api/csrfFetch.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Country options — `labelKey` resolves via t() at render time. */
const COUNTRY_OPTIONS = [
  { value: 'VN', labelKey: 'register.country.VN' },
  { value: 'US', labelKey: 'register.country.US' },
  { value: 'GB', labelKey: 'register.country.GB' },
  { value: 'JP', labelKey: 'register.country.JP' },
  { value: 'KR', labelKey: 'register.country.KR' },
  { value: 'CN', labelKey: 'register.country.CN' },
  { value: 'TH', labelKey: 'register.country.TH' },
  { value: 'SG', labelKey: 'register.country.SG' },
  { value: 'AU', labelKey: 'register.country.AU' },
  { value: 'DE', labelKey: 'register.country.DE' },
  { value: 'FR', labelKey: 'register.country.FR' },
] as const;

const SUPPORTED_COUNTRIES: ReadonlySet<string> = new Set(
  COUNTRY_OPTIONS.map((c) => c.value as string),
);

const SHIPPING_MODES = ['self_ship', 'printway_processed', 'hybrid'] as const;
type ShippingMode = (typeof SHIPPING_MODES)[number];

interface PublicTechnique {
  id: string;
  code: string;
  name: Record<string, string>;
  type: string;
}

const PasswordPolicy = z
  .string()
  .min(12, i18nT('auth:register.validation.passwordMin'))
  .max(128, i18nT('auth:register.validation.passwordMax'))
  .refine((p) => /[A-Z]/.test(p), i18nT('auth:register.validation.passwordUpper'))
  .refine((p) => /[a-z]/.test(p), i18nT('auth:register.validation.passwordLower'))
  .refine((p) => /\d/.test(p), i18nT('auth:register.validation.passwordDigit'))
  .refine((p) => /[^A-Za-z0-9]/.test(p), i18nT('auth:register.validation.passwordSpecial'));

const Step1Schema = z.object({
  email: z
    .string()
    .min(1, i18nT('auth:register.validation.emailRequired'))
    .email(i18nT('auth:register.validation.emailInvalid'))
    .max(255, i18nT('auth:register.validation.emailMax')),
  // Wave 12 Task 88 (DEC-B5): phone is OPTIONAL per design.
  phone: z.string().optional().or(z.literal('')),
  password: PasswordPolicy,
  displayName: z
    .string()
    .min(1, i18nT('auth:register.validation.displayNameRequired'))
    .max(120, i18nT('auth:register.validation.displayNameMax')),
  legalName: z
    .string()
    .min(2, i18nT('auth:register.validation.legalNameRequired'))
    .max(160, i18nT('auth:register.validation.legalNameMax')),
  countryCode: z
    .string()
    .length(2, i18nT('auth:register.validation.countryLen'))
    .refine((c) => SUPPORTED_COUNTRIES.has(c), i18nT('auth:register.validation.countryUnsupported')),
  timezone: z
    .string()
    .min(3, i18nT('auth:register.validation.timezoneRequired'))
    .max(64, i18nT('auth:register.validation.timezoneRequired'))
    .regex(/^[A-Za-z_]+\/[A-Za-z_+\-\d/]+$/, i18nT('auth:register.validation.timezoneInvalid')),
  acceptTerms: z.literal(true, {
    message: i18nT('auth:register.validation.termsRequired'),
  }),
});
type Step1Input = z.infer<typeof Step1Schema>;

const Step2Schema = z.object({
  techniques: z
    .array(z.string())
    .min(1, i18nT('auth:register.validation.techniquesRequired')),
  shippingMode: z.enum(SHIPPING_MODES),
  warehouseName: z
    .string()
    .min(2, i18nT('auth:register.validation.warehouseNameRequired'))
    .max(80, i18nT('auth:register.validation.warehouseNameMax')),
  addressLine1: z
    .string()
    .min(2, i18nT('auth:register.validation.addressLine1Required'))
    .max(200, i18nT('auth:register.validation.addressLine1Max')),
  addressLine2: z.string().max(200).optional().or(z.literal('')),
  city: z
    .string()
    .min(1, i18nT('auth:register.validation.cityRequired'))
    .max(80, i18nT('auth:register.validation.cityMax')),
  region: z.string().max(80).optional().or(z.literal('')),
  postalCode: z.string().max(20).optional().or(z.literal('')),
  warehouseCountry: z
    .string()
    .length(2, i18nT('auth:register.validation.countryLen'))
    .refine((c) => SUPPORTED_COUNTRIES.has(c), i18nT('auth:register.validation.countryUnsupported')),
});
type Step2Input = z.infer<typeof Step2Schema>;

// reCAPTCHA bypass token honored by dev BE (per CONTEXT dev policy
// + API-CONTRACTS sample token). Real reCAPTCHA v3 token injection is the
// job of a Wave 10 task wiring grecaptcha.execute(...) into App.tsx.
const DEV_RECAPTCHA_TOKEN = 'mock-recaptcha-token-bypass-dev';

/** Browser timezone as the sensible default (falls back to VN). */
function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Ho_Chi_Minh';
  } catch {
    return 'Asia/Ho_Chi_Minh';
  }
}

/** IANA zone list from the runtime (supported everywhere we target). */
function listTimezones(): string[] {
  try {
    const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    if (typeof anyIntl.supportedValuesOf === 'function') {
      return anyIntl.supportedValuesOf('timeZone');
    }
  } catch {
    // fall through
  }
  return [detectTimezone()];
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t, i18n } = useTranslation('auth');

  const [step, setStep] = useState<1 | 2>(1);
  const [step1Values, setStep1Values] = useState<Step1Input | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Default country from `?country=XX` query (uppercased + validated against
  // SUPPORTED_COUNTRIES) else 'VN'.
  const defaultCountry = useMemo<string>(() => {
    const raw = searchParams.get('country');
    if (raw) {
      const upper = raw.toUpperCase();
      if (SUPPORTED_COUNTRIES.has(upper)) return upper;
    }
    return 'VN';
  }, [searchParams]);

  const timezoneOptions = useMemo(() => listTimezones(), []);

  const step1Form = useForm<Step1Input>({
    resolver: zodResolver(Step1Schema),
    defaultValues: {
      email: '',
      phone: '',
      password: '',
      displayName: '',
      legalName: '',
      countryCode: defaultCountry,
      timezone: detectTimezone(),
      // acceptTerms intentionally omitted — Zod literal(true) treats undefined
      // as invalid; lets unchecked checkbox surface the error message.
    } as Partial<Step1Input> as Step1Input,
  });

  const step2Form = useForm<Step2Input>({
    resolver: zodResolver(Step2Schema),
    defaultValues: {
      techniques: [],
      shippingMode: 'self_ship',
      warehouseName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      region: '',
      postalCode: '',
      warehouseCountry: defaultCountry,
    },
  });

  // Sync defaultCountry into forms when query-string changes after mount.
  useEffect(() => {
    step1Form.setValue('countryCode', defaultCountry);
    step2Form.setValue('warehouseCountry', defaultCountry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry]);

  // Active techniques — fetched only when step 2 is shown; public endpoint,
  // no auth. Error state renders a retry button (never silent).
  const techniquesQuery = useQuery({
    queryKey: ['public-techniques'],
    enabled: step === 2,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<PublicTechnique[]> => {
      // csrfFetch (không phải fetch trần): vhost vendor-v3 KHÔNG proxy `/api/`,
      // fetch tương đối sẽ nhận SPA index.html 200 text/html → JSON parse fail.
      // csrfFetch prefix VITE_API_BASE_URL nên request đi đúng api-v3.
      const res = await csrfFetch('/api/v1/public/techniques', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP_${res.status}`);
      const body = (await res.json()) as {
        data?: { techniques?: PublicTechnique[] };
      };
      return body.data?.techniques ?? [];
    },
  });

  const locale = i18n.language?.slice(0, 2) ?? 'en';
  const techniqueLabel = (tech: PublicTechnique): string =>
    tech.name?.[locale] ?? tech.name?.en ?? tech.code;

  const onStep1Submit = (values: Step1Input): void => {
    // Phone E.164 normalization (FIX-05). Wave 12 Task 88: phone now optional.
    if (values.phone && values.phone.trim().length > 0) {
      try {
        normalizePhoneE164(values.phone, values.countryCode);
      } catch (err) {
        const msg =
          err instanceof PhoneFormatError
            ? t('register.validation.phoneInvalid')
            : t('register.validation.phoneNormalizeFailed');
        step1Form.setError('phone', { type: 'manual', message: msg });
        return;
      }
    }
    setStep1Values(values);
    setSubmitError(null);
    setStep(2);
  };

  const onStep2Submit = async (step2: Step2Input): Promise<void> => {
    if (!step1Values) {
      setStep(1);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);

    let phoneE164 = '';
    if (step1Values.phone && step1Values.phone.trim().length > 0) {
      try {
        phoneE164 = normalizePhoneE164(
          step1Values.phone,
          step1Values.countryCode,
        );
      } catch {
        setStep(1);
        setSubmitting(false);
        return;
      }
    }

    try {
      // Idempotency-Key: rotate per submit. UUID v4 keeps the BE
      // anon-scoped middleware from collapsing distinct retries by the same
      // user — per CONTEXT L1576-1582.
      const idempotencyKey = generateIdempotencyKey();

      const client = apiClient();
      const { data, error, response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown; headers?: Record<string, string> },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST('/api/v1/auth/register', {
        body: {
          email: step1Values.email.trim().toLowerCase(),
          password: step1Values.password,
          displayName: step1Values.displayName.trim(),
          legalName: step1Values.legalName.trim(),
          phone: phoneE164,
          countryCode: step1Values.countryCode,
          timezone: step1Values.timezone,
          intendedRole: 'vendor',
          acceptTerms: true,
          recaptchaToken: DEV_RECAPTCHA_TOKEN,
          vendorDetails: {
            shippingMode: step2.shippingMode,
            techniques: step2.techniques,
            warehouse: {
              name: step2.warehouseName.trim(),
              address_line1: step2.addressLine1.trim(),
              address_line2: step2.addressLine2?.trim() || undefined,
              city: step2.city.trim(),
              region: step2.region?.trim() || undefined,
              postal_code: step2.postalCode?.trim() || undefined,
              country: step2.warehouseCountry,
            },
          },
        },
        headers: {
          'Idempotency-Key': idempotencyKey,
          'Content-Type': 'application/json',
        },
      });

      if (error || !response.ok) {
        const code = extractErrorCode(error) ?? `HTTP_${response.status}`;
        const msg = humanizeRegisterError(code, extractErrorDetails(error));
        setSubmitError(msg);
        toast({
          variant: 'error',
          title: t('register.toast.errorTitle'),
          description: msg,
        });
        return;
      }

      // Persist email for /verify-otp page (sessionStorage so a tab refresh
      // mid-flow keeps the user oriented; cross-tab leak is not possible).
      try {
        sessionStorage.setItem(
          'registerEmail',
          step1Values.email.trim().toLowerCase(),
        );
        const payload = unwrap<{
          otpChannel?: string;
          resendAvailableAt?: string;
        }>(data);
        if (payload.otpChannel) {
          sessionStorage.setItem('registerOtpChannel', payload.otpChannel);
        }
        if (payload.resendAvailableAt) {
          sessionStorage.setItem(
            'registerResendAvailableAt',
            payload.resendAvailableAt,
          );
        }
      } catch {
        // sessionStorage unavailable (privacy mode) — degrade gracefully.
      }

      toast({
        variant: 'success',
        title: t('register.toast.successTitle'),
        description: t('register.toast.successDescription'),
      });
      navigate('/verify-otp');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t('register.toast.errorFallback');
      setSubmitError(msg);
      toast({
        variant: 'error',
        title: t('register.toast.errorTitle'),
        description: msg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const s1 = step1Form.formState.errors;
  const s2 = step2Form.formState.errors;

  return (
    <AuthSplitLayout variant="register">
      <div className="flex flex-1 items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('register.headingVendor')}</CardTitle>
            <CardDescription>
              {t('register.subVendor')}
              <span className="mt-1 block text-xs text-pw-text-muted">
                {t('register.stepIndicator', { current: step, total: 2 })}
                {' — '}
                {step === 1 ? t('register.step1Title') : t('register.step2Title')}
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <Form onSubmit={step1Form.handleSubmit(onStep1Submit)} noValidate>
                <FormField>
                  <FormLabel htmlFor="register-email">{t('register.labelEmail')}</FormLabel>
                  <Input
                    id="register-email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={Boolean(s1.email)}
                    {...step1Form.register('email')}
                  />
                  {s1.email ? <FormMessage>{s1.email.message}</FormMessage> : null}
                </FormField>

                <FormField>
                  <FormLabel htmlFor="register-displayName">{t('register.labelDisplayName')}</FormLabel>
                  <Input
                    id="register-displayName"
                    type="text"
                    autoComplete="name"
                    aria-invalid={Boolean(s1.displayName)}
                    {...step1Form.register('displayName')}
                  />
                  {s1.displayName ? (
                    <FormMessage>{s1.displayName.message}</FormMessage>
                  ) : null}
                </FormField>

                <FormField>
                  <FormLabel htmlFor="register-legalName">{t('register.labelLegalName')}</FormLabel>
                  <Input
                    id="register-legalName"
                    type="text"
                    autoComplete="organization"
                    aria-invalid={Boolean(s1.legalName)}
                    {...step1Form.register('legalName')}
                  />
                  {s1.legalName ? (
                    <FormMessage>{s1.legalName.message}</FormMessage>
                  ) : null}
                </FormField>

                <FormField>
                  <FormLabel htmlFor="register-country">{t('register.labelCountry')}</FormLabel>
                  <Controller
                    control={step1Form.control}
                    name="countryCode"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="register-country">
                          <SelectValue placeholder={t('register.labelCountry')} />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_OPTIONS.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              {t(c.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {s1.countryCode ? (
                    <FormMessage>{s1.countryCode.message}</FormMessage>
                  ) : null}
                </FormField>

                <FormField>
                  <FormLabel htmlFor="register-timezone">{t('register.labelTimezone')}</FormLabel>
                  <Controller
                    control={step1Form.control}
                    name="timezone"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="register-timezone">
                          <SelectValue placeholder={t('register.labelTimezone')} />
                        </SelectTrigger>
                        <SelectContent>
                          {timezoneOptions.map((tz) => (
                            <SelectItem key={tz} value={tz}>
                              {tz}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {s1.timezone ? (
                    <FormMessage>{s1.timezone.message}</FormMessage>
                  ) : null}
                </FormField>

                <FormField>
                  <FormLabel htmlFor="register-phone">
                    {t('register.labelPhone')}
                    <span className="ml-1 text-xs font-normal text-pw-text-muted">
                      {t('register.phoneOptionalNote')}
                    </span>
                  </FormLabel>
                  <Input
                    id="register-phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder={t('register.phonePlaceholder')}
                    aria-invalid={Boolean(s1.phone)}
                    {...step1Form.register('phone')}
                  />
                  {s1.phone ? <FormMessage>{s1.phone.message}</FormMessage> : null}
                </FormField>

                <FormField>
                  <FormLabel htmlFor="register-password">{t('register.labelPassword')}</FormLabel>
                  <Input
                    id="register-password"
                    type="password"
                    autoComplete="new-password"
                    aria-invalid={Boolean(s1.password)}
                    {...step1Form.register('password')}
                  />
                  <p className="text-xs text-pw-text-muted">
                    {t('register.passwordHint')}
                  </p>
                  {s1.password ? (
                    <FormMessage>{s1.password.message}</FormMessage>
                  ) : null}
                </FormField>

                <FormField>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      aria-invalid={Boolean(s1.acceptTerms)}
                      {...step1Form.register('acceptTerms')}
                    />
                    <span>
                      {t('register.termsPrefix')}{' '}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noreferrer"
                        className="text-pw-primary-strong underline"
                      >
                        {t('register.termsLink')}
                      </a>{' '}
                      {t('register.termsAnd')}{' '}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noreferrer"
                        className="text-pw-primary-strong underline"
                      >
                        {t('register.privacyLink')}
                      </a>
                      .
                    </span>
                  </label>
                  {s1.acceptTerms ? (
                    <FormMessage>{s1.acceptTerms.message}</FormMessage>
                  ) : null}
                </FormField>

                <Button type="submit" variant="default" size="lg" className="w-full">
                  {t('register.next')}
                </Button>
              </Form>
            ) : (
              <Form onSubmit={step2Form.handleSubmit(onStep2Submit)} noValidate>
                <FormField>
                  <FormLabel>{t('register.techniques.label')}</FormLabel>
                  {techniquesQuery.isLoading ? (
                    <div className="flex items-center gap-2 py-2 text-sm text-pw-text-muted">
                      <Spinner size="sm" />
                      {t('register.techniques.loading')}
                    </div>
                  ) : techniquesQuery.isError ? (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                      <span>{t('register.techniques.loadError')}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void techniquesQuery.refetch()}
                      >
                        {t('register.techniques.retry')}
                      </Button>
                    </div>
                  ) : (
                    <Controller
                      control={step2Form.control}
                      name="techniques"
                      render={({ field }) => (
                        <div className="grid grid-cols-2 gap-2">
                          {(techniquesQuery.data ?? []).map((tech) => {
                            const checked = field.value.includes(tech.code);
                            return (
                              <label
                                key={tech.code}
                                className="flex items-center gap-2 text-sm"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={submitting}
                                  onChange={() =>
                                    field.onChange(
                                      checked
                                        ? field.value.filter((c) => c !== tech.code)
                                        : [...field.value, tech.code],
                                    )
                                  }
                                />
                                <span>{techniqueLabel(tech)}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    />
                  )}
                  {s2.techniques ? (
                    <FormMessage>{s2.techniques.message}</FormMessage>
                  ) : null}
                </FormField>

                <FormField>
                  <FormLabel htmlFor="register-shippingMode">
                    {t('register.shippingMode.label')}
                  </FormLabel>
                  <Controller
                    control={step2Form.control}
                    name="shippingMode"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(v) => field.onChange(v as ShippingMode)}
                        disabled={submitting}
                      >
                        <SelectTrigger id="register-shippingMode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SHIPPING_MODES.map((mode) => (
                            <SelectItem key={mode} value={mode}>
                              {t(`register.shippingMode.options.${mode}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>

                <div className="rounded-md border border-pw-border p-3">
                  <p className="mb-2 text-sm font-medium">
                    {t('register.warehouse.title')}
                  </p>

                  <FormField>
                    <FormLabel htmlFor="register-whName">
                      {t('register.warehouse.name')}
                    </FormLabel>
                    <Input
                      id="register-whName"
                      type="text"
                      aria-invalid={Boolean(s2.warehouseName)}
                      disabled={submitting}
                      {...step2Form.register('warehouseName')}
                    />
                    {s2.warehouseName ? (
                      <FormMessage>{s2.warehouseName.message}</FormMessage>
                    ) : null}
                  </FormField>

                  <FormField>
                    <FormLabel htmlFor="register-whAddr1">
                      {t('register.warehouse.addressLine1')}
                    </FormLabel>
                    <Input
                      id="register-whAddr1"
                      type="text"
                      autoComplete="address-line1"
                      aria-invalid={Boolean(s2.addressLine1)}
                      disabled={submitting}
                      {...step2Form.register('addressLine1')}
                    />
                    {s2.addressLine1 ? (
                      <FormMessage>{s2.addressLine1.message}</FormMessage>
                    ) : null}
                  </FormField>

                  <FormField>
                    <FormLabel htmlFor="register-whAddr2">
                      {t('register.warehouse.addressLine2')}
                    </FormLabel>
                    <Input
                      id="register-whAddr2"
                      type="text"
                      autoComplete="address-line2"
                      disabled={submitting}
                      {...step2Form.register('addressLine2')}
                    />
                  </FormField>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField>
                      <FormLabel htmlFor="register-whCity">
                        {t('register.warehouse.city')}
                      </FormLabel>
                      <Input
                        id="register-whCity"
                        type="text"
                        aria-invalid={Boolean(s2.city)}
                        disabled={submitting}
                        {...step2Form.register('city')}
                      />
                      {s2.city ? <FormMessage>{s2.city.message}</FormMessage> : null}
                    </FormField>
                    <FormField>
                      <FormLabel htmlFor="register-whRegion">
                        {t('register.warehouse.region')}
                      </FormLabel>
                      <Input
                        id="register-whRegion"
                        type="text"
                        disabled={submitting}
                        {...step2Form.register('region')}
                      />
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField>
                      <FormLabel htmlFor="register-whZip">
                        {t('register.warehouse.zip')}
                      </FormLabel>
                      <Input
                        id="register-whZip"
                        type="text"
                        autoComplete="postal-code"
                        disabled={submitting}
                        {...step2Form.register('postalCode')}
                      />
                    </FormField>
                    <FormField>
                      <FormLabel htmlFor="register-whCountry">
                        {t('register.warehouse.country')}
                      </FormLabel>
                      <Controller
                        control={step2Form.control}
                        name="warehouseCountry"
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={submitting}
                          >
                            <SelectTrigger id="register-whCountry">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {COUNTRY_OPTIONS.map((c) => (
                                <SelectItem key={c.value} value={c.value}>
                                  {t(c.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {s2.warehouseCountry ? (
                        <FormMessage>{s2.warehouseCountry.message}</FormMessage>
                      ) : null}
                    </FormField>
                  </div>
                </div>

                {submitError ? (
                  <div
                    role="alert"
                    className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
                  >
                    {submitError}
                  </div>
                ) : null}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="flex-1"
                    disabled={submitting}
                    onClick={() => setStep(1)}
                  >
                    {t('register.back')}
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    className="flex-1"
                    disabled={submitting}
                  >
                    {submitting ? <Spinner size="sm" /> : t('register.submit')}
                  </Button>
                </div>
              </Form>
            )}
          </CardContent>
          <CardFooter className="justify-center">
            <p className="text-sm text-pw-text-muted">
              {t('register.haveAccount')}{' '}
              <a href="/login" className="text-pw-primary-strong underline">
                {t('register.loginLink')}
              </a>
            </p>
          </CardFooter>
        </Card>
      </div>
    </AuthSplitLayout>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as { success?: boolean }).success === true &&
    'data' in payload
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function extractErrorCode(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    (payload as { error?: { code?: string } }).error?.code
  ) {
    return (payload as { error: { code: string } }).error.code;
  }
  return null;
}

function extractErrorDetails(payload: unknown): Record<string, string> {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    typeof (payload as { error?: { details?: unknown } }).error?.details ===
      'object'
  ) {
    return (payload as { error: { details: Record<string, string> } }).error
      .details;
  }
  return {};
}

function humanizeRegisterError(
  code: string,
  details?: Record<string, string>,
): string {
  switch (code) {
    case 'AUTH_EMAIL_TAKEN':
    case 'AUTH_EMAIL_ALREADY_EXISTS':
      return i18nT('auth:register.error.emailTaken');
    case 'AUTH_VENDOR_REJECTED_COOLDOWN': {
      const raw = details?.reapply_available_at;
      const date = raw ? new Date(raw).toLocaleDateString(undefined) : '';
      return i18nT('auth:register.error.rejectedCooldown', { date });
    }
    case 'AUTH_PASSWORD_WEAK':
      return i18nT('auth:register.error.passwordWeak');
    case 'AUTH_RECAPTCHA_FAILED':
      return i18nT('auth:register.error.recaptchaFailed');
    case 'RATE_LIMIT':
      return i18nT('auth:register.error.rateLimit');
    case 'VALIDATION_FAILED':
      return i18nT('auth:register.error.validation');
    case 'IDEMPOTENCY_KEY_CONFLICT':
      return i18nT('auth:register.error.idempotencyConflict');
    default:
      return i18nT('auth:register.error.fallback', { code });
  }
}
